import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { 
  TrendingUp, TrendingDown, Minus, Target, Brain, AlertTriangle,
  CheckCircle2, Clock, Calendar, Zap, Star, BookOpen, ChevronRight,
  Lightbulb, Activity, Award
} from 'lucide-react';
import { 
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ParentInsights = ({ childId }) => {
  const [loading, setLoading] = useState(true);
  const [insights, setInsights] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (childId) {
      loadInsights();
    }
  }, [childId]);

  const loadInsights = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/parent/insights/${childId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInsights(response.data);
      setError(null);
    } catch (err) {
      console.error('Error loading insights:', err);
      setError('Unable to load insights');
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <Card className="shadow-lg">
        <CardContent className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        </CardContent>
      </Card>
    );
  }

  if (error || !insights) {
    return (
      <Card className="shadow-lg bg-gray-50">
        <CardContent className="text-center py-8 text-gray-500">
          <Brain className="h-12 w-12 mx-auto mb-3 opacity-50" />
          <p>{error || 'No insights available yet'}</p>
          <p className="text-sm mt-2">Your child needs to complete the diagnostic assessment first.</p>
        </CardContent>
      </Card>
    );
  }

  const { capabilityBands, insights: insightData, recentSessions, childName, yearGroup } = insights;

  // Prepare data for charts
  const bandData = Object.entries(capabilityBands || {}).map(([subject, data]) => ({
    subject: subject.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase()),
    score: data.score || 50,
    band: data.band,
    accuracy: data.accuracy || 0
  }));

  const COLORS = {
    stretch: '#10B981',
    secure: '#3B82F6',
    foundation: '#F59E0B'
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'improving': return <TrendingUp className="h-5 w-5 text-green-500" />;
      case 'needs_attention': return <TrendingDown className="h-5 w-5 text-red-500" />;
      default: return <Minus className="h-5 w-5 text-gray-400" />;
    }
  };

  const getTrendColor = (trend) => {
    switch (trend) {
      case 'improving': return 'text-green-600 bg-green-50';
      case 'needs_attention': return 'text-red-600 bg-red-50';
      default: return 'text-blue-600 bg-blue-50';
    }
  };

  const getFrequencyColor = (freq) => {
    switch (freq) {
      case 'excellent': return 'bg-green-100 text-green-700';
      case 'good': return 'bg-blue-100 text-blue-700';
      case 'needs_more': return 'bg-amber-100 text-amber-700';
      default: return 'bg-red-100 text-red-700';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header Overview */}
      <Card className="shadow-lg bg-gradient-to-br from-purple-50 to-indigo-50 border-purple-200">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-xl flex items-center gap-2">
                <Brain className="h-6 w-6 text-purple-600" />
                {childName}'s Learning Profile
              </CardTitle>
              <CardDescription>{yearGroup?.replace('-', ' ').toUpperCase()}</CardDescription>
            </div>
            <div className={`px-4 py-2 rounded-lg ${getTrendColor(insightData?.progressTrend)}`}>
              <div className="flex items-center gap-2">
                {getTrendIcon(insightData?.progressTrend)}
                <span className="font-medium capitalize">
                  {insightData?.progressTrend?.replace('_', ' ') || 'Stable'}
                </span>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg p-4 text-center">
              <Calendar className="h-6 w-6 text-purple-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-gray-800">{insightData?.practiceDaysThisWeek || 0}</p>
              <p className="text-xs text-gray-500">Practice Days</p>
            </div>
            <div className="bg-white rounded-lg p-4 text-center">
              <Target className="h-6 w-6 text-blue-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-gray-800">{insightData?.totalQuestionsThisWeek || 0}</p>
              <p className="text-xs text-gray-500">Questions</p>
            </div>
            <div className="bg-white rounded-lg p-4 text-center">
              <Activity className="h-6 w-6 text-green-500 mx-auto mb-2" />
              <Badge className={getFrequencyColor(insightData?.practiceFrequency)}>
                {insightData?.practiceFrequency?.replace('_', ' ') || 'N/A'}
              </Badge>
              <p className="text-xs text-gray-500 mt-1">Frequency</p>
            </div>
            <div className="bg-white rounded-lg p-4 text-center">
              <Star className="h-6 w-6 text-amber-500 mx-auto mb-2" />
              <p className="text-lg font-semibold text-gray-800 capitalize">{insightData?.confidenceLevel || 'N/A'}</p>
              <p className="text-xs text-gray-500">Confidence</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Capability Bands */}
      <Card className="shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Award className="h-5 w-5 text-purple-600" />
            Subject Performance
          </CardTitle>
          <CardDescription>Capability bands based on diagnostic and practice sessions</CardDescription>
        </CardHeader>
        <CardContent>
          {bandData.length > 0 ? (
            <div className="space-y-4">
              {bandData.map((item) => (
                <div key={item.subject} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{item.subject}</span>
                    <div className="flex items-center gap-2">
                      <Badge 
                        variant="outline" 
                        className={`
                          ${item.band === 'stretch' ? 'bg-green-100 text-green-700 border-green-300' : ''}
                          ${item.band === 'secure' ? 'bg-blue-100 text-blue-700 border-blue-300' : ''}
                          ${item.band === 'foundation' ? 'bg-amber-100 text-amber-700 border-amber-300' : ''}
                        `}
                      >
                        {item.band}
                      </Badge>
                      <span className="text-sm text-gray-500">{item.accuracy}% accuracy</span>
                    </div>
                  </div>
                  <Progress 
                    value={item.score} 
                    className="h-2"
                  />
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-gray-500 py-4">
              Complete the diagnostic assessment to see subject performance
            </p>
          )}
        </CardContent>
      </Card>

      {/* Struggle Areas & Strong Areas */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Struggle Areas */}
        <Card className="shadow-lg border-l-4 border-l-amber-400">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg text-amber-700">
              <AlertTriangle className="h-5 w-5" />
              Areas to Focus
            </CardTitle>
          </CardHeader>
          <CardContent>
            {insightData?.struggleAreas?.length > 0 ? (
              <div className="space-y-3">
                {insightData.struggleAreas.map((area, idx) => (
                  <div key={idx} className="bg-amber-50 rounded-lg p-3">
                    <p className="font-medium capitalize">{area.subject.replace('-', ' ')}</p>
                    {area.weakConcepts?.length > 0 && (
                      <p className="text-sm text-amber-700 mt-1">
                        Focus on: {area.weakConcepts.join(', ').replace(/_/g, ' ')}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-4 text-gray-500">
                <CheckCircle2 className="h-8 w-8 mx-auto mb-2 text-green-400" />
                <p>No major struggle areas identified</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Strong Areas */}
        <Card className="shadow-lg border-l-4 border-l-green-400">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg text-green-700">
              <Star className="h-5 w-5" />
              Strengths
            </CardTitle>
          </CardHeader>
          <CardContent>
            {insightData?.strongAreas?.length > 0 ? (
              <div className="space-y-3">
                {insightData.strongAreas.map((area, idx) => (
                  <div key={idx} className="bg-green-50 rounded-lg p-3 flex items-center justify-between">
                    <p className="font-medium capitalize">{area.subject.replace('-', ' ')}</p>
                    <Badge className="bg-green-100 text-green-700">{area.accuracy}%</Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-4 text-gray-500">
                <Lightbulb className="h-8 w-8 mx-auto mb-2 text-amber-400" />
                <p>Keep practicing to identify strengths</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recommendations */}
      {insightData?.recommendations?.length > 0 && (
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Lightbulb className="h-5 w-5 text-amber-500" />
              Recommendations
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {insightData.recommendations.map((rec, idx) => (
                <div 
                  key={idx} 
                  className={`p-4 rounded-lg border-l-4 ${
                    rec.priority === 'high' ? 'bg-red-50 border-l-red-400' :
                    rec.priority === 'medium' ? 'bg-amber-50 border-l-amber-400' :
                    'bg-green-50 border-l-green-400'
                  }`}
                >
                  <p className="font-medium">{rec.message}</p>
                  <p className="text-sm text-gray-600 mt-1">{rec.action}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Sessions */}
      {recentSessions?.length > 0 && (
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Clock className="h-5 w-5 text-purple-600" />
              Recent Practice Sessions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {recentSessions.slice(0, 5).map((session, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium capitalize">{session.subject?.replace('-', ' ')}</p>
                    <p className="text-sm text-gray-500">
                      {new Date(session.completedAt).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold">
                      {session.totalCorrect}/{session.totalAnswered}
                    </p>
                    <p className="text-sm text-gray-500">
                      {Math.round((session.totalCorrect / session.totalAnswered) * 100)}% correct
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ParentInsights;
